import { VerifyPayeeCheckResponseCode } from '@seapoint/server/enums';
import * as Sentry from '@sentry/react';
import { CircleAlert, CircleCheck, TriangleAlert } from 'lucide-react';
import { ComponentProps, ElementType } from 'react';
import { Inline } from '../../../../design-system/inline/inline';
import { Stack } from '../../../../design-system/stack/stack';
import { cn } from '../../../../utils/cn';
import { exhaustiveCheck } from '../../../../utils/exhaustive-check';

type Severity = 'SUCCESS' | 'WARNING' | 'ERROR';

type PayeeCheckResultVariantConfig =
  | { variant: VerifyPayeeCheckResponseCode.CLOSE_MATCH; name?: string }
  | {
      variant: Exclude<VerifyPayeeCheckResponseCode, VerifyPayeeCheckResponseCode.CLOSE_MATCH>;
    }
  | {
      variant: 'CONFIRMATION';
    };

type PayeeCheckResultCardProps = {
  variantConfig: PayeeCheckResultVariantConfig;
};

export const PayeeCheckResultCard = ({ variantConfig }: PayeeCheckResultCardProps) => {
  const { severity, icon, title, description, footNote } = getCardContent(variantConfig);

  return (
    <Inline
      space={3}
      wrap="nowrap"
      align="start"
      className="rounded-card border-[0.5px] border-seapoint-gray-border bg-white px-4 py-3"
    >
      <div
        className={cn('h-10 w-10 flex-shrink-0 rounded-full p-2.5', PAYEE_CHECK_COLORS[severity].backgroundClassName)}
      >
        {icon}
      </div>

      <Stack space={4}>
        <Stack space={1}>
          <p
            className={cn(
              'text-base font-semibold leading-6 tracking-tighter-custom',
              PAYEE_CHECK_COLORS[severity].textClassName,
            )}
          >
            {title}
          </p>

          {description}
        </Stack>

        {footNote && (
          <p className="text-sm font-medium leading-[22px] tracking-tighter-custom text-seapoint-gray-text">
            {footNote}
          </p>
        )}
      </Stack>
    </Inline>
  );
};

const PAYEE_CHECK_COLORS: Record<Severity, { hex: string; textClassName: string; backgroundClassName: string }> = {
  SUCCESS: {
    hex: '#169B62',
    textClassName: 'text-seapoint-green-badge',
    backgroundClassName: 'bg-seapoint-green-badge/10',
  },
  WARNING: {
    hex: '#FF883E',
    textClassName: 'text-seapoint-orange-badge',
    backgroundClassName: 'bg-seapoint-orange-badge/10',
  },
  ERROR: {
    hex: '#FF531D',
    textClassName: 'text-seapoint-red',
    backgroundClassName: 'bg-seapoint-red/10',
  },
};

const getCardContent = (variantConfig: ComponentProps<typeof PayeeCheckResultCard>['variantConfig']) => {
  const { variant } = variantConfig;

  const textClassName = 'text-sm font-medium leading-[22px] tracking-[-0.28px] text-seapoint-gray-text';

  switch (variant) {
    case VerifyPayeeCheckResponseCode.MATCHED: {
      const severity = 'SUCCESS' as const;

      return {
        severity,
        icon: createBadgeIcon(CircleCheck, PAYEE_CHECK_COLORS[severity].hex),
        title: 'Account matched',
        description: <p className={textClassName}>The account details you've entered are matched.</p>,
      };
    }
    case VerifyPayeeCheckResponseCode.CLOSE_MATCH: {
      const severity = 'WARNING' as const;

      if (!variantConfig.name) {
        Sentry.captureMessage(
          "CLOSE_MATCH payee check result doesn't have matched name. Showing result card without name and the name when saving the beneficiary will be undefined",
          {
            extra: {
              variantConfig,
            },
          },
        );
      }

      const description = variantConfig.name ? (
        <p className={textClassName}>
          The name on the account is <span className="text-black">{variantConfig.name}</span>. Click change and save and
          we'll change it to the right name for you.
        </p>
      ) : (
        <p className={textClassName}>
          The name on the account doesn't match the name you've entered. Click change and save and we'll change it to
          the right name for you.
        </p>
      );

      return {
        severity,
        icon: createBadgeIcon(CircleAlert, PAYEE_CHECK_COLORS[severity].hex),
        title: "The name you entered isn't the name on the account",
        description,
        footNote: "Only save anyway if you're sure this is the right account.",
      };
    }
    case VerifyPayeeCheckResponseCode.NO_MATCH: {
      const severity = 'ERROR' as const;

      return {
        severity,
        icon: createBadgeIcon(TriangleAlert, PAYEE_CHECK_COLORS[severity].hex),
        title: "The name doesn't match",
        description: (
          <p className={textClassName}>
            The name on the account doesn't match the name you've entered. Double-check the details you have and try
            again.
          </p>
        ),
        footNote: "Only save anyway if you're sure this is the right account.",
      };
    }
    case VerifyPayeeCheckResponseCode.CHECK_NOT_POSSIBLE:
    case VerifyPayeeCheckResponseCode.INVALID:
    case VerifyPayeeCheckResponseCode.GENERAL:
    case VerifyPayeeCheckResponseCode.INTERNAL_ERROR: {
      const severity = 'ERROR' as const;

      return {
        severity,
        icon: createBadgeIcon(TriangleAlert, PAYEE_CHECK_COLORS[severity].hex),
        title: "We're unable to verify these account details",
        description: (
          <p className={textClassName}>We can't check the account name right now and recommend you try again later.</p>
        ),
        footNote: "Only continue anyway if you're sure this is the right account.",
      };
    }
    case 'CONFIRMATION': {
      const severity = 'ERROR' as const;

      return {
        severity,
        icon: createBadgeIcon(CircleAlert, PAYEE_CHECK_COLORS[severity].hex),
        title: 'Are you sure you want to continue?',
        description: (
          <p className={textClassName}>
            Paying this person or business may lead to your money being sent to the wrong account.
          </p>
        ),
        footNote: 'We may not be able to recover this money for you.',
      };
    }
    default:
      return exhaustiveCheck(variant);
  }
};

const createBadgeIcon = (Icon: ElementType, color: string) => (
  <Icon color={color} strokeWidth={1.5} className="h-full w-full" />
);
